import { useEffect, useImperativeHandle, useRef } from "react";
import Box from "@mui/material/Box";
import { useTheme } from "@mui/material";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Mention from "@tiptap/extension-mention";
import { useTranslationApp } from "../../../lib/i18next";
import { createAnnotationMentionSuggestion } from "../utils/annotationMentionSuggestion";
import {
  parseMarkupToTipTapDoc,
  serializeTipTapDocToMarkup,
} from "../utils/mentionTipTapAdapters";

//eslint-disable-next-line
export const MENTION_TEXTAREA_LAYOUT = {
  fontSize: 14,
  lineHeight: 1.43,
  padding: 8,
  minHeight: 56,
  maxHeight: 200,
};

const AnnotationMentionTextarea = ({
  value = "",
  onChange = () => {},
  onSubmit = () => {},
  placeholder = "",
  disabled = false,
  ownersByID = {},
  excludeUserID = null,
  inputRef = null,
  editorRef = null,
}) => {
  const theme = useTheme();
  const { t } = useTranslationApp();
  const wrapperRef = useRef(null);
  const isSuggestionOpenRef = useRef(false);
  const skipNextValueSyncRef = useRef(false);
  const onChangeRef = useRef(onChange);
  const onSubmitRef = useRef(onSubmit);
  const ownersByIDRef = useRef(ownersByID);
  const excludeUserIDRef = useRef(excludeUserID);
  const tRef = useRef(t);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    onSubmitRef.current = onSubmit;
  }, [onSubmit]);

  useEffect(() => {
    ownersByIDRef.current = ownersByID;
  }, [ownersByID]);

  useEffect(() => {
    excludeUserIDRef.current = excludeUserID;
  }, [excludeUserID]);

  useEffect(() => {
    tRef.current = t;
  }, [t]);

  const editor = useEditor({
    immediatelyRender: false,
    extensions: [
      StarterKit.configure({
        blockquote: false,
        codeBlock: false,
        heading: false,
        horizontalRule: false,
        listItem: false,
        bulletList: false,
        orderedList: false,
        code: false,
        bold: false,
        italic: false,
        strike: false,
        dropcursor: false,
        gapcursor: false,
      }),
      Mention.configure({
        HTMLAttributes: {
          class: "annotation-mention",
        },
        deleteTriggerWithBackspace: true,
        renderText: ({ node }) => `@${node.attrs.label ?? node.attrs.id}`,
        suggestion: createAnnotationMentionSuggestion({
          getOwnersByID: () => ownersByIDRef.current,
          getExcludeUserID: () => excludeUserIDRef.current,
          getT: () => tRef.current,
          onOpenChange: (isOpen) => {
            isSuggestionOpenRef.current = isOpen;
          },
        }),
      }),
    ],
    content: parseMarkupToTipTapDoc(value),
    editable: !disabled,
    editorProps: {
      attributes: {
        class: "annotation-mention-editor",
      },
      handleKeyDown: (_view, event) => {
        if (
          (event.key === "Enter" || event.code === "NumpadEnter") &&
          !event.shiftKey &&
          !event.isComposing
        ) {
          if (isSuggestionOpenRef.current) {
            return false;
          }

          event.preventDefault();
          onSubmitRef.current();
          return true;
        }

        return false;
      },
    },
    onUpdate: ({ editor: currentEditor }) => {
      const markup = serializeTipTapDocToMarkup(currentEditor.getJSON());
      onChangeRef.current(null, markup);
    },
  });

  useImperativeHandle(
    editorRef,
    () => ({
      clear: () => {
        if (!editor) return;
        skipNextValueSyncRef.current = true;
        editor.commands.clearContent(true);
      },
      focus: () => {
        editor?.commands.focus("end");
      },
    }),
    [editor]
  );

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!disabled);
  }, [disabled, editor]);

  useEffect(() => {
    if (!editor) return;

    if (typeof inputRef === "function") {
      inputRef(editor.view.dom);
    } else if (inputRef) {
      inputRef.current = editor.view.dom;
    }
  }, [editor, inputRef]);

  useEffect(() => {
    if (!editor) return;

    if (skipNextValueSyncRef.current) {
      skipNextValueSyncRef.current = false;
      return;
    }

    const nextValue = value || "";
    const currentMarkup = serializeTipTapDocToMarkup(editor.getJSON());
    if (currentMarkup === nextValue) return;

    if (!nextValue) {
      editor.commands.clearContent(false);
      return;
    }

    editor.commands.setContent(parseMarkupToTipTapDoc(nextValue), {
      emitUpdate: false,
    });
  }, [editor, value]);

  const showPlaceholder = !(value || "").trim();

  return (
    <Box
      ref={wrapperRef}
      sx={{
        position: "relative",
        width: "100%",
        minHeight: MENTION_TEXTAREA_LAYOUT.minHeight,
        maxHeight: MENTION_TEXTAREA_LAYOUT.maxHeight,
        "& .ProseMirror": {
          outline: "none",
          border: "1px solid transparent",
          boxSizing: "border-box",
          margin: 0,
          padding: `${MENTION_TEXTAREA_LAYOUT.padding}px`,
          minHeight: MENTION_TEXTAREA_LAYOUT.minHeight,
          maxHeight: MENTION_TEXTAREA_LAYOUT.maxHeight,
          height: "auto",
          overflowX: "hidden",
          overflowY: "auto",
          fontFamily: theme.typography.fontFamily,
          fontSize: MENTION_TEXTAREA_LAYOUT.fontSize,
          lineHeight: MENTION_TEXTAREA_LAYOUT.lineHeight,
          fontWeight: 400,
          color: theme.palette.text.primary,
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          cursor: disabled ? "not-allowed" : "text",
          opacity: disabled ? 0.6 : 1,
        },
        "& .ProseMirror p": {
          margin: 0,
        },
        "& .ProseMirror .annotation-mention": {
          fontWeight: 700,
          color: theme.palette.text.primary,
        },
      }}
    >
      {showPlaceholder ? (
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            top: MENTION_TEXTAREA_LAYOUT.padding,
            left: MENTION_TEXTAREA_LAYOUT.padding,
            right: MENTION_TEXTAREA_LAYOUT.padding,
            fontFamily: theme.typography.fontFamily,
            fontSize: MENTION_TEXTAREA_LAYOUT.fontSize,
            lineHeight: MENTION_TEXTAREA_LAYOUT.lineHeight,
            color: "text.disabled",
            pointerEvents: "none",
            userSelect: "none",
          }}
        >
          {placeholder}
        </Box>
      ) : null}

      <Box sx={{ position: "relative", zIndex: 1 }}>
        <EditorContent editor={editor} />
      </Box>
    </Box>
  );
};

export default AnnotationMentionTextarea;
